import React, { ReactNode } from "react";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import { Modal as MuiModal, Backdrop, Fade } from "@material-ui/core";

import Spinner from "./Spinner";

export type ModalSize =
  | "extra-small"
  | "x-small"
  | "xs"
  | "small"
  | "medium"
  | "large"
  | "extra-large"
  | "x-large"
  | "xl"
  | "width-1000";

const SIZE_ALIASES: Record<string, string> = {
  "extra-small": "extra-small",
  "x-small": "extra-small",
  xs: "extra-small",
  small: "small",
  medium: "medium",
  large: "large",
  "extra-large": "extra-large",
  "x-large": "extra-large",
  xl: "extra-large",
  "width-1000": "extra-large",
};

interface ModalProps {
  visible: boolean;
  children: ReactNode | string;
  className?: string;
  size?: ModalSize;
  title?: ReactNode | string;
  closeButton?: boolean;
  closeButtonCross?: boolean;
  closeOnBackDrop?: boolean;
  onClose: () => void;
  loading?: boolean;
  loadingTip?: string;
  [key: string]: any;
}

const Modal = ({
  className,
  visible,
  size = "medium",
  children,
  title,
  closeButton = true,
  closeButtonCross,
  closeOnBackDrop,
  onClose,
  loading,
  loadingTip,
  onRendered,
  ...props
}: ModalProps) => {
  const canonicalSize = SIZE_ALIASES[size] || "medium";
  const sizeClasses = [
    `modal-${canonicalSize}`,
    canonicalSize === "extra-large" ? "modal-x-large modal-width-1000" : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  const _handleCloseOnBackDrop = () => {
    if (closeOnBackDrop) {
      _handleCloseModal();
    }
  };

  const _handleCloseModal = () => {
    onClose();
  };

  const showHeader = Boolean(title);

  return (
    // @ts-ignore
    <MuiModal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className="eh-modal"
      open={visible}
      onClose={_handleCloseOnBackDrop}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      onRendered={onRendered}
    >
      <Fade in={visible}>
        <div className={`modal-body-container ${sizeClasses}`}>
          {closeButtonCross && !showHeader && (
            <CloseIcon className="close-icon" onClick={_handleCloseModal} />
          )}
          {showHeader && (
            <div className="modal-header">
              <h4 id="transition-modal-title" className="modal-title">
                {title}
              </h4>
              {closeButton && (
                <IconButton
                  aria-label="close"
                  className="modal-close-btn"
                  onClick={_handleCloseModal}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </div>
          )}
          <div className="modal-body">
            <Spinner visible={loading} loadingTip={loadingTip}>
              {children}
            </Spinner>
          </div>
        </div>
      </Fade>
    </MuiModal>
  );
};

Modal.defaultProps = {
  visible: false,
  closeButton: true,
  closeOnBackDrop: true,
  size: "medium",
  loading: false,
};

export default Modal;
